import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus } from 'lucide-react';

import { Button } from 'components/ui/button';
import { VisibilityField } from 'components/blocks/visibility-field';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Textarea } from 'components/ui/textarea';
import { useToast } from 'hooks/use-toast';
import { cn } from 'lib/utils';
import { useTranslation } from 'react-i18next';
import { useCreateFolder } from 'features/chat/hooks/use-kb';
import { useMyPlan } from 'features/subscription/hooks/use-seats';
import { useGetLlmModels } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { IGetAgentModelResponse } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { CHUNKING_STRATEGIES } from '../constants';
import { getDefaultEmbeddingModel, getEmbeddingModelLabel, sortEmbeddingModels } from '../utils';
import ConfigurationButton from 'components/configuration/ConfigurationButton';
import { DEFAULT_RESOURCE_VISIBILITY } from 'types/visibility';

/** Keep in sync with validation and helper text below. */
const FOLDER_NAME_MAX_LENGTH = 50;
const FOLDER_DESCRIPTION_MAX_LENGTH = 200;

const createAddFolderSchema = (translate: (key: string, options?: Record<string, unknown>) => string) =>
  z.object({
    name: z
      .string()
      .trim()
      .min(1, translate('NAME_REQUIRED', { defaultValue: 'Name is required' }))
      .max(
        FOLDER_NAME_MAX_LENGTH,
        translate('NAME_MAX_50_CHARACTERS', {
          defaultValue: `Name must be at most ${FOLDER_NAME_MAX_LENGTH} characters`,
        })
      ),
    description: z
      .string()
      .trim()
      .min(1, translate('DESCRIPTION_REQUIRED', { defaultValue: 'Description is required' }))
      .max(
        FOLDER_DESCRIPTION_MAX_LENGTH,
        translate('DESCRIPTION_MAX_200_CHARACTERS', {
          defaultValue: `Description cannot exceed ${FOLDER_DESCRIPTION_MAX_LENGTH} characters`,
        })
      ),
    model_name: z
      .string()
      .trim()
      .min(1, translate('EMBEDDING_MODEL_REQUIRED', { defaultValue: 'Embedding model is required' })),
    provider: z.string().trim().min(1, translate('PROVIDER_REQUIRED', { defaultValue: 'Provider is required' })),
    chunking_strategy: z
      .string()
      .trim()
      .min(1, translate('CHUNKING_STRATEGY_REQUIRED', { defaultValue: 'Chunking strategy is required' })),
    visibility: z.enum(['private', 'public']),
  });

type AddFolderForm = z.infer<ReturnType<typeof createAddFolderSchema>>;

export function AddFolderModal({
  projectKey,
  // triggerClassName,
  triggerLabel,
  isCentered = false,
}: {
  projectKey: string;
  // triggerClassName?: string;
  triggerLabel?: string;
  /** Center the button and its no-plan note (empty state); otherwise they line up on the right. */
  isCentered?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const addFolderSchema = useMemo(() => createAddFolderSchema(t), [t]);
  const { toast } = useToast();
  const { mutateAsync: createFolder, isPending } = useCreateFolder();
  const { data: llmModels } = useGetLlmModels();
  const { hasPlan, isLoading: isPlanLoading } = useMyPlan();

  const embeddingModels = useMemo(
    () => sortEmbeddingModels((llmModels || []).filter((model) => model.model_type === 'embedding')),
    [llmModels]
  );

  const form = useForm<AddFolderForm>({
    resolver: zodResolver(addFolderSchema),
    defaultValues: {
      name: '',
      description: '',
      model_name: '',
      provider: '',
      chunking_strategy: 'semantic',
      visibility: DEFAULT_RESOURCE_VISIBILITY,
    },
  });

  useEffect(() => {
    const preferredModel = getDefaultEmbeddingModel(llmModels);
    if (!preferredModel) {
      return;
    }

    if (!form.getValues('model_name')) {
      form.setValue('model_name', preferredModel.model_name);
      form.setValue('provider', preferredModel.provider);
    }
  }, [form, llmModels]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      form.reset({
        name: '',
        description: '',
        model_name: getDefaultEmbeddingModel(llmModels)?.model_name || '',
        provider: getDefaultEmbeddingModel(llmModels)?.provider || '',
        chunking_strategy: 'semantic',
        visibility: DEFAULT_RESOURCE_VISIBILITY,
      });
    }
  };

  const onSubmit = async (values: AddFolderForm) => {
    try {
      await createFolder({
        project_key: projectKey,
        name: values.name,
        description: values.description,
        visibility: values.visibility,
        embedding_model_config: {
          Provider: values.provider,
          Model: values.model_name,
          ChunkingStrategy: values.chunking_strategy,
          IsProviderBlocks: true,
          ModelId: null,
        },
      });

      toast({
        variant: 'success',
        title: t('FOLDER_CREATED', { defaultValue: 'Folder created' }),
        description: t('KB_FOLDER_READY', { defaultValue: 'Your knowledge base folder is ready.' }),
      });
      handleOpenChange(false);
    } catch (error) {
      toast({
        title: t('FAILED_TO_CREATE_FOLDER', { defaultValue: 'Failed to create folder' }),
        description:
          error instanceof Error
            ? error.message
            : t('PLEASE_TRY_AGAIN', { defaultValue: 'Please try again.' }),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <div className={cn('flex flex-col gap-1', isCentered ? 'items-center text-center' : 'items-end text-right')}>
        <DialogTrigger asChild>
          <ConfigurationButton disabled={!projectKey || !hasPlan}>
            <Plus size={20} className="sm:mr-2" />
            {triggerLabel || t('ADD_FOLDER') || 'Add Folder'}
          </ConfigurationButton>
        </DialogTrigger>
        {/* Wait for the plan check so the note doesn't flash for people who have a plan. */}
        {!isPlanLoading && !hasPlan && (
          <p className="text-xs text-muted-foreground">
            {t('GET_PLAN_OR_TOP_UP', { defaultValue: 'Get a plan or top up to continue.' })}
          </p>
        )}
      </div>
      <DialogContent className="flex max-h-[min(92dvh,100svh)] w-[calc(100vw-1.5rem)] max-w-2xl flex-col gap-4 overflow-hidden p-5">
        <DialogHeader className="shrink-0">
          <DialogTitle>{t('CREATE_KNOWLEDGE_FOLDER') || 'Create Knowledge Folder'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-1">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => {
                  const len = field.value?.length ?? 0;
                  return (
                    <FormItem>
                      <FormLabel>{t('FOLDER_NAME') || 'Folder Name'}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('FOLDER_NAME_PLACEHOLDER') || 'Product docs'}
                          maxLength={FOLDER_NAME_MAX_LENGTH}
                          aria-describedby="kb-folder-name-char-hint"
                          {...field}
                        />
                      </FormControl>
                      <div className="flex items-start justify-between gap-2">
                        <FormDescription id="kb-folder-name-char-hint" className="!mt-1.5">
                          {t('MAX_CHARS_HELPER', {
                            max: FOLDER_NAME_MAX_LENGTH,
                            defaultValue: 'Maximum {{max}} characters',
                          })}
                        </FormDescription>
                        <span
                          className="text-muted-foreground shrink-0 pt-1.5 text-xs tabular-nums"
                          aria-live="polite"
                        >
                          {len}/{FOLDER_NAME_MAX_LENGTH}
                        </span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => {
                  const len = field.value?.length ?? 0;
                  return (
                    <FormItem>
                      <FormLabel>{t('DESCRIPTION') || 'Description'}</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={t('FOLDER_DESCRIPTION_PLACEHOLDER') || 'What should this folder contain?'}
                          height="110px"
                          maxLength={FOLDER_DESCRIPTION_MAX_LENGTH}
                          aria-describedby="kb-folder-description-char-hint"
                          {...field}
                        />
                      </FormControl>
                      <div className="flex items-start justify-between gap-2">
                        <FormDescription id="kb-folder-description-char-hint" className="!mt-1.5">
                          {t('MAX_CHARS_HELPER', {
                            max: FOLDER_DESCRIPTION_MAX_LENGTH,
                            defaultValue: 'Maximum {{max}} characters',
                          })}
                        </FormDescription>
                        <span
                          className="text-muted-foreground shrink-0 pt-1.5 text-xs tabular-nums"
                          aria-live="polite"
                        >
                          {len}/{FOLDER_DESCRIPTION_MAX_LENGTH}
                        </span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="visibility"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <VisibilityField
                        value={field.value}
                        onValueChange={field.onChange}
                        idPrefix="add-kb-folder-visibility"
                        disabled={isPending}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="model_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('EMBEDDING_MODEL') || 'Embedding Model'}</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={(value) => {
                          const selectedModel = embeddingModels.find((model) => model.model_name === value);
                          field.onChange(value);
                          form.setValue('provider', selectedModel?.provider || '');
                        }}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t('SELECT_MODEL') || 'Select a model'} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {embeddingModels.map((model) => (
                            <SelectItem key={`${model.provider}-${model.model_name}`} value={model.model_name}>
                              {getEmbeddingModelLabel(model, t)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="chunking_strategy"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('CHUNKING_STRATEGY') || 'Chunking Strategy'}</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t('SELECT_STRATEGY') || 'Select a strategy'} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CHUNKING_STRATEGIES.map((strategy) => (
                            <SelectItem key={strategy.value} value={strategy.value}>
                              {t(strategy.labelKey) || strategy.defaultLabel}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <DialogFooter className="shrink-0 border-t border-border/40 pt-4">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                {t('CANCEL') || 'Cancel'}
              </Button>
              <Button type="submit" loading={isPending} disabled={isPending || embeddingModels.length === 0}>
                {t('CREATE_FOLDER') || 'Create Folder'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
